import * as React from 'react';
import axios, { AxiosError, AxiosInstance } from 'axios';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { useAuth } from 'context/auth-provider';

type ActionKey = 'list' | 'sync' | 'bootstrap' | 'status';

interface ApiResult {
  action: ActionKey;
  status: number;
  statusText: string;
  data: unknown;
  isError: boolean;
  severity?: 'success' | 'error' | 'warning';
}

interface PartitionRow {
  name?: string;
  mode?: string;
  sourceType?: string;
}

interface ListPartitionsResponse {
  message?: string;
  table?: string;
  partitionCount?: number;
  partitions?: PartitionRow[];
  error?: unknown;
}

interface SyncPartitionsResponse {
  message?: string;
  totalClientsEvaluated?: number;
  createdCount?: number;
  notPresentCount?: number;
  updatedClientIds?: string[];
  notPresentClientIds?: string[];
  error?: unknown;
}

interface SyncStatusRow {
  clientId: string;
  status: 'updated' | 'not-present';
}

interface PartitionStatusRow {
  clientId: string;
  isPresentInPowerBiData: boolean;
  isPresentInPowerBiClientsTable: boolean;
  isPresentInAws: boolean;
}

interface PartitionStatusResponse {
  message: string;
  dataPartitionCount: number;
  clientsTablePartitionCount: number;
  clientCount: number;
  rows: PartitionStatusRow[];
}

const TABLE_NAME = 'data';
const CLIENTS_TABLE_NAME = 'clients_table';
const BOOTSTRAP_TIMEOUT_MESSAGE =
  'Partition creation request accepted. It will take some time to create partitions, so try checking status first.';

const ACTION_LABEL: Record<ActionKey, string> = {
  list: 'List PowerBI partitions',
  sync: 'Sync powerBI Partitions',
  bootstrap: 'Create missing partitions in powerBI',
  status: 'Get Partition status',
};

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const extractPartitionNames = (payload: unknown): string[] => {
  if (!isObjectRecord(payload) || !Array.isArray(payload.partitions)) return [];

  return payload.partitions
    .map((partition): string => {
      if (!isObjectRecord(partition)) return '';
      if (typeof partition.name === 'string') return partition.name.trim();
      return '';
    })
    .filter((name) => name.length > 0);
};

const extractClientIds = (payload: unknown): string[] => {
  if (!isObjectRecord(payload)) return [];

  const source =
    (Array.isArray(payload.items) && payload.items) ||
    (Array.isArray(payload.Items) && payload.Items) ||
    (Array.isArray(payload.clients) && payload.clients) ||
    [];

  return source
    .map((client): string => {
      if (typeof client === 'string') return client.trim();
      if (!isObjectRecord(client)) return '';

      if (typeof client.client_id === 'string') return client.client_id.trim();
      if (typeof client.ClientId === 'string') return client.ClientId.trim();

      return '';
    })
    .filter((clientId) => clientId.length > 0);
};

const normalizeId = (value: string): string => value.trim().toLowerCase();

const toIdMap = (ids: string[]): Map<string, string> => {
  const map = new Map<string, string>();
  ids.forEach((id) => {
    const normalized = normalizeId(id);
    if (!normalized || map.has(normalized)) return;
    map.set(normalized, id.trim());
  });
  return map;
};

const getErrorStatusText = (status: number | undefined): string => {
  if (status === 401 || status === 403) return 'Unauthorized';
  if (status === 404) return 'Not Found';
  if (status === 500) return 'Server Error';
  return 'Request Failed';
};

const isTimeoutLikeError = (axiosError: AxiosError): boolean => {
  const status = axiosError.response?.status;
  const code = axiosError.code || '';
  const message = (axiosError.message || '').toLowerCase();

  return (
    status === 408 ||
    status === 504 ||
    status === 524 ||
    code === 'ECONNABORTED' ||
    code === 'ETIMEDOUT' ||
    message.includes('timeout')
  );
};

export default function AdminUtilityView() {
  const { user } = useAuth();
  const [license, setLicense] = React.useState('');
  const [isAuthorized, setIsAuthorized] = React.useState(false);
  const [activeAction, setActiveAction] = React.useState<ActionKey | null>(null);
  const [result, setResult] = React.useState<ApiResult | null>(null);

  const axiosInstance = React.useMemo<AxiosInstance | null>(() => {
    if (!user) return null;
    return axios.create({
      baseURL: process.env.REACT_APP_AMA_V2_API_URL,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: user.idToken,
      },
    });
  }, [user]);

  const handleAuthorize = () => {
    setIsAuthorized(license.trim().length > 0);
    setResult(null);
  };

  const isActionDisabled = !isAuthorized;

  const listPartitionsData = React.useMemo<ListPartitionsResponse | null>(() => {
    if (!result || result.action !== 'list' || result.isError) return null;
    if (!result.data || typeof result.data !== 'object') return null;

    const payload = result.data as ListPartitionsResponse;
    if (!Array.isArray(payload.partitions)) return null;
    return payload;
  }, [result]);

  const syncPartitionsData = React.useMemo<SyncPartitionsResponse | null>(() => {
    if (!result || result.action !== 'sync' || result.isError) return null;
    if (!result.data || typeof result.data !== 'object') return null;

    const payload = result.data as SyncPartitionsResponse;
    if (!Array.isArray(payload.updatedClientIds)) return null;
    return payload;
  }, [result]);

  const syncStatusRows = React.useMemo<SyncStatusRow[]>(() => {
    if (!syncPartitionsData) return [];

    const updatedRows = (syncPartitionsData.updatedClientIds || []).map((clientId) => ({
      clientId,
      status: 'updated' as const,
    }));

    const notPresentRows = (syncPartitionsData.notPresentClientIds || []).map((clientId) => ({
      clientId,
      status: 'not-present' as const,
    }));

    return [...updatedRows, ...notPresentRows];
  }, [syncPartitionsData]);

  const partitionStatusData = React.useMemo<PartitionStatusResponse | null>(() => {
    if (!result || result.action !== 'status' || result.isError) return null;
    if (!result.data || typeof result.data !== 'object') return null;

    const payload = result.data as PartitionStatusResponse;
    if (!Array.isArray(payload.rows)) return null;

    return payload;
  }, [result]);

  const runAction = async (action: ActionKey): Promise<void> => {
    if (!axiosInstance || !license.trim()) {
      setResult({
        action,
        status: 401,
        statusText: 'Not authenticated',
        data: { message: 'You must be logged in and authorise license first.' },
        isError: true,
      });
      return;
    }

    setActiveAction(action);
    setResult(null);

    try {
      let response;
      const headers = { 'x-admin-license': license.trim() };

      if (action === 'list') {
        response = await axiosInstance.get('/partitions/powerbi', {
          headers,
          params: { table: TABLE_NAME },
        });
      } else if (action === 'status') {
        const [dataPartitionsResponse, clientsTablePartitionsResponse, clientsResponse] = await Promise.all([
          axiosInstance.get('/partitions/powerbi', {
            headers,
            params: { table: TABLE_NAME },
          }),
          axiosInstance.get('/partitions/powerbi', {
            headers,
            params: { table: CLIENTS_TABLE_NAME },
          }),
          axiosInstance.get('/clients', {
            headers,
          }),
        ]);

        const dataPartitionNames = extractPartitionNames(dataPartitionsResponse.data);
        const clientsTablePartitionNames = extractPartitionNames(clientsTablePartitionsResponse.data);
        const clientIds = extractClientIds(clientsResponse.data);

        const dataPartitionMap = toIdMap(dataPartitionNames);
        const clientsTablePartitionMap = toIdMap(clientsTablePartitionNames);
        const clientMap = toIdMap(clientIds);

        const mergedKeys = new Set<string>([
          ...Array.from(dataPartitionMap.keys()),
          ...Array.from(clientsTablePartitionMap.keys()),
          ...Array.from(clientMap.keys()),
        ]);
        const rows: PartitionStatusRow[] = Array.from(mergedKeys)
          .sort((a, b) => a.localeCompare(b))
          .map((normalizedId) => ({
            clientId:
              clientMap.get(normalizedId) ||
              dataPartitionMap.get(normalizedId) ||
              clientsTablePartitionMap.get(normalizedId) ||
              normalizedId,
            isPresentInPowerBiData: dataPartitionMap.has(normalizedId),
            isPresentInPowerBiClientsTable: clientsTablePartitionMap.has(normalizedId),
            isPresentInAws: clientMap.has(normalizedId),
          }));

        response = {
          status: 200,
          statusText: 'OK',
          data: {
            message: 'Partition status compared across PowerBI tables and AWS clients.',
            dataPartitionCount: dataPartitionMap.size,
            clientsTablePartitionCount: clientsTablePartitionMap.size,
            clientCount: clientMap.size,
            rows,
          },
        };
      } else if (action === 'sync') {
        response = await axiosInstance.post(
          '/partitions/powerbi/sync-status',
          {
            table: TABLE_NAME,
            updateOnlyActive: true,
          },
          { headers }
        );
      } else {
        const bootstrapResponse = await axiosInstance.post(
          '/partitions/powerbi/bootstrap-active-clients',
          {},
          { headers }
        );
        response = {
          status: bootstrapResponse.status,
          statusText: bootstrapResponse.statusText || 'OK',
          data: bootstrapResponse.data,
        };
      }

      setResult({
        action,
        status: response.status,
        statusText: response.statusText || 'Success',
        data: response.data,
        isError: false,
        severity: 'success',
      });
    } catch (error: unknown) {
      const axiosError = error as AxiosError;

      if (action === 'bootstrap' && (isTimeoutLikeError(axiosError) || !axiosError.response)) {
        setResult({
          action,
          status: axiosError.response?.status || 202,
          statusText: 'Processing',
          data: { message: BOOTSTRAP_TIMEOUT_MESSAGE },
          isError: false,
          severity: 'warning',
        });
        return;
      }

      setResult({
        action,
        status: axiosError.response?.status || 0,
        statusText:
          axiosError.response?.statusText || getErrorStatusText(axiosError.response?.status),
        data: axiosError.response?.data || { message: axiosError.message },
        isError: true,
        severity: 'error',
      });
    } finally {
      setActiveAction(null);
    }
  };

  return (
    <React.Fragment>
      <Grid item xs={12}>
        <Paper
          sx={{
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 'calc(100vh - 170px)',
            overflowY: 'auto',
          }}
        >
          <Stack spacing={3}>
            <Typography variant="h4" component="h1">
              Admin Utility
            </Typography>

            <Box>
              <TextField
                fullWidth
                type="password"
                label="License"
                value={license}
                onChange={(event) => {
                  setLicense(event.target.value);
                  setIsAuthorized(false);
                  setResult(null);
                }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                You need authorise license to use this utility
              </Typography>
              <Button
                variant="contained"
                sx={{ mt: 2 }}
                onClick={handleAuthorize}
                disabled={license.trim().length === 0}
              >
                Authorise
              </Button>
            </Box>

            <Box>
              <Button
                variant="contained"
                disabled={isActionDisabled || activeAction !== null}
                onClick={() => runAction('status')}
                startIcon={
                  activeAction === 'status' ? <CircularProgress size={18} color="inherit" /> : undefined
                }
              >
                {ACTION_LABEL.status}
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Compares partitions across PowerBI &quot;data&quot; table, PowerBI &quot;clients_table&quot;, and AWS DB — shows where each client/partition ID is present
              </Typography>
            </Box>

            <Box>
              <Button
                variant="contained"
                disabled={isActionDisabled || activeAction !== null}
                onClick={() => runAction('list')}
                startIcon={
                  activeAction === 'list' ? <CircularProgress size={18} color="inherit" /> : undefined
                }
              >
                {ACTION_LABEL.list}
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This functionality will only fetch the partitions from PowerBI and list
              </Typography>
            </Box>

            <Box>
              <Button
                variant="contained"
                disabled={isActionDisabled || activeAction !== null}
                onClick={() => runAction('sync')}
                startIcon={
                  activeAction === 'sync' ? <CircularProgress size={18} color="inherit" /> : undefined
                }
              >
                {ACTION_LABEL.sync}
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This functionality will update the status of clients -&gt; partitions flag to created if partitions for particular client is present in powerBI already, otherwise it will set partition created flag as blank
              </Typography>
            </Box>

            <Box>
              <Button
                variant="contained"
                disabled={isActionDisabled || activeAction !== null}
                onClick={() => runAction('bootstrap')}
                startIcon={
                  activeAction === 'bootstrap' ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : undefined
                }
              >
                {ACTION_LABEL.bootstrap}
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This functionality will read current active clients and create the partitions in powerBI if missing or not present
              </Typography>
            </Box>

            {result && (
              <Box>
                <Alert severity={result.severity || (result.isError ? 'error' : 'success')} sx={{ mb: 1 }}>
                  {ACTION_LABEL[result.action]}: {result.status} {result.statusText}
                </Alert>
                {listPartitionsData ? (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {listPartitionsData.message || 'PowerBI partitions fetched'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Table: {listPartitionsData.table || TABLE_NAME} | Partition Count:{' '}
                      {listPartitionsData.partitionCount ?? listPartitionsData.partitions?.length ?? 0}
                    </Typography>
                    <TableContainer sx={{ maxHeight: 320 }}>
                      <Table stickyHeader size="small" aria-label="PowerBI partitions table">
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Mode</TableCell>
                            <TableCell>Source Type</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {listPartitionsData.partitions?.map((partition, index) => (
                            <TableRow key={`${partition.name || 'partition'}-${index}`}>
                              <TableCell>{partition.name || '-'}</TableCell>
                              <TableCell>{partition.mode || '-'}</TableCell>
                              <TableCell>{partition.sourceType || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                ) : partitionStatusData ? (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {partitionStatusData.message}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      PowerBI &quot;data&quot;: {partitionStatusData.dataPartitionCount} | PowerBI &quot;clients_table&quot;: {partitionStatusData.clientsTablePartitionCount} | AWS Clients: {partitionStatusData.clientCount}
                    </Typography>
                    <TableContainer sx={{ maxHeight: 320 }}>
                      <Table stickyHeader size="small" aria-label="partition status table">
                        <TableHead>
                          <TableRow>
                            <TableCell>Client / Partition ID</TableCell>
                            <TableCell>PowerBI &quot;data&quot;</TableCell>
                            <TableCell>PowerBI &quot;clients_table&quot;</TableCell>
                            <TableCell>AWS DB</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {partitionStatusData.rows.map((row) => (
                            <TableRow key={row.clientId}>
                              <TableCell>{row.clientId}</TableCell>
                              <TableCell>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                                  {row.isPresentInPowerBiData ? (
                                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                                  ) : (
                                    <RemoveCircleOutlineIcon color="error" fontSize="small" />
                                  )}
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    color={row.isPresentInPowerBiData ? 'success.main' : 'error.main'}
                                  >
                                    {row.isPresentInPowerBiData ? 'Yes' : 'No'}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                                  {row.isPresentInPowerBiClientsTable ? (
                                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                                  ) : (
                                    <RemoveCircleOutlineIcon color="error" fontSize="small" />
                                  )}
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    color={row.isPresentInPowerBiClientsTable ? 'success.main' : 'error.main'}
                                  >
                                    {row.isPresentInPowerBiClientsTable ? 'Yes' : 'No'}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                                  {row.isPresentInAws ? (
                                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                                  ) : (
                                    <RemoveCircleOutlineIcon color="error" fontSize="small" />
                                  )}
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    color={row.isPresentInAws ? 'success.main' : 'error.main'}
                                  >
                                    {row.isPresentInAws ? 'Yes' : 'No'}
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                ) : syncPartitionsData ? (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {syncPartitionsData.message || 'PowerBI partition status sync complete'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Total Clients Evaluated: {syncPartitionsData.totalClientsEvaluated ?? 0} | Created:
                      {' '}
                      {syncPartitionsData.createdCount ?? 0} | Not Present: {syncPartitionsData.notPresentCount ?? 0}
                    </Typography>
                    <TableContainer sx={{ maxHeight: 320 }}>
                      <Table stickyHeader size="small" aria-label="updated client ids table">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width:     80 }}>#</TableCell>
                            <TableCell>Client ID</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {syncStatusRows.map((row, index) => (
                            <TableRow key={`${row.clientId}-${row.status}-${index}`}>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>{row.clientId}</TableCell>
                              <TableCell>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                                  {row.status === 'updated' ? (
                                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                                  ) : (
                                    <RemoveCircleOutlineIcon color="warning" fontSize="small" />
                                  )}
                                  <Typography
                                    component="sub"
                                    variant="caption"
                                    color={row.status === 'updated' ? 'success.main' : 'warning.main'}
                                  >
                                    {row.status === 'updated' ? 'Updated' : 'Not present'}
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {syncStatusRows.length === 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Client-level status rows are not available in this response payload.
                      </Typography>
                    )}
                  </Paper>
                ) : result.action === 'bootstrap' && result.severity === 'warning' ? (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="body2">{BOOTSTRAP_TIMEOUT_MESSAGE}</Typography>
                  </Paper>
                ) : (
                  <Paper variant="outlined" sx={{ p: 2, maxHeight: 320, overflow: 'auto' }}>
                    <Typography
                      variant="body2"
                      component="pre"
                      sx={{ m: 0, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
                    >
                      {JSON.stringify(result.data, null, 2)}
                    </Typography>
                  </Paper>
                )}
              </Box>
            )}
          </Stack>
        </Paper>
      </Grid>
    </React.Fragment>
  );
}